import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from '_common/hooks/redux';
import { openAndUpdateModal } from '_common/modals/ModalsSlice';
import EditorManager from 'Editor/services/EditorManager';

import ColorsMenu from 'Editor/components/ColorsMenu/ColorsMenu';
import { Popover, Tooltip } from '@ets/design-system';
import { SplitToggle } from '_common/components';
import { CustomIcon } from '_common/lib/components';
import { EditorDOMUtils } from 'Editor/services/_Common/DOM';
import { ReduxInterface } from 'Editor/services';
import { getFormattedColor } from 'assets/colors';

const FontBackgroundColor = ({ isReadOnlyMode }: { isReadOnlyMode: boolean }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [documentColors, setDocumentColors] = useState<
    { r: string | number; g: string | number; b: string | number; rgb: string }[]
  >([]);
  const [open, setOpen] = useState(false);

  const fontBackgroundColor = useSelector((state) => {
    const highlightColor = state.editor.toolbar.highlightColor;
    if (highlightColor) {
      if (typeof highlightColor === 'boolean') {
        return !highlightColor ? 'transparent' : '';
      } else {
        return highlightColor.replace(/ /g, '');
      }
    }

    return '';
  });

  const onBackgroundColorChange = (color: string) => {
    ReduxInterface.updateToolbarValues({
      highlightColor: color,
    });
    applyColor(color);
  };

  const applyColor = (color: string) => {
    EditorManager.getInstance().toggleSelectionStyle('highlightColor', color);
  };

  const onClearBackgroundColor = () => {
    ReduxInterface.updateToolbarValues({
      highlightColor: 'transparent',
    });
    EditorManager.getInstance().removeSelectionStyle('highlightColor', null);
  };

  const handleToggleClick = (onClick: { (): void }) => {
    const documentColors = EditorDOMUtils.getDocumentColors();
    if (documentColors) {
      setDocumentColors(documentColors);
    }
    setOpen(!open);
    onClick();
  };

  const openColorPickerModal = () => {
    dispatch(
      openAndUpdateModal({
        modal: 'ColorPickerModal',
        data: {
          action: 'toggleSelectionStyle',
          actionValues: { style: 'highlightColor' },
        },
      }),
    );
  };

  return (
    <Popover.Root placement="bottom-start" open={open} onOpenChange={setOpen}>
      <Popover.Trigger>
        <Tooltip content={intl.formatMessage({ id: 'HIGHLIGHT_COLOR' })} testId="font-background">
          <div>
            <SplitToggle
              isToggled={false}
              isOpen={open}
              disabled={isReadOnlyMode}
              testId="font-background"
              onClick={() => {
                applyColor(fontBackgroundColor);
                setOpen(false);
              }}
              customIcon={
                <CustomIcon
                  name="TextBackgroundColor"
                  color={fontBackgroundColor ? getFormattedColor(fontBackgroundColor) : '#6D6D82'}
                  size={24}
                />
              }
              onPopperClick={() => handleToggleClick(() => setOpen(!open))}
              margin="2px"
            />
          </div>
        </Tooltip>
      </Popover.Trigger>
      <Popover.Content testId="highlight-color">
        <ColorsMenu
          toggle={() => setOpen(false)}
          changeBackgroundColor={(color: string) => {
            onBackgroundColorChange(color);
            setOpen(false);
          }}
          clearBackgroundColor={() => {
            onClearBackgroundColor();
            setOpen(false);
          }}
          documentColors={documentColors}
          selected={fontBackgroundColor}
          setOpenColorPickerModal={openColorPickerModal}
          attribute={undefined}
        />
      </Popover.Content>
    </Popover.Root>
  );
};

export default FontBackgroundColor;
